import { publicApiRequest } from '../../lib/hooks/axios-instance'
import { addTokenToHeaders } from '../../lib/utils/addTokenReq'

export interface SavedContact {
  id: number
  user_id: string
  name: string
  email?: string
  phone: string
  payment_method: 'MTN' | 'ORANGE' | 'WALLET'
  usage_count: number
  last_used?: string
  created_at: string
  updated_at: string
}

export interface CreateContactRequest {
  name: string
  email?: string
  phone: string
  payment_method: 'MTN' | 'ORANGE' | 'WALLET'
}

export interface UpdateContactRequest {
  name?: string
  email?: string
}

export interface FindContactRequest {
  phone: string
  payment_method: 'MTN' | 'ORANGE' | 'WALLET'
}

export class SavedContactsService {
  /**
   * Get all saved contacts for the authenticated user
   * @param token - JWT token
   * @param limit - Optional limit for number of contacts (default: all)
   * @param sort - Optional sort order: 'most_active' or 'recent'
   */
  public async getContacts(token: string, limit?: number, sort?: 'most_active' | 'recent') {
    let url = '/saved-contacts/api/contacts'
    const params = new URLSearchParams()

    if (limit) params.append('limit', limit.toString())
    if (sort) params.append('sort', sort)

    if (params.toString()) url += `?${params.toString()}`

    return await (await publicApiRequest()).get(url, addTokenToHeaders(token))
  }

  /**
   * Get a single contact by ID
   */
  public async getContact(contactId: number, token: string) {
    return await (
      await publicApiRequest()
    ).get(`/saved-contacts/api/contacts/${contactId}`, addTokenToHeaders(token))
  }

  /**
   * Create a new saved contact
   */
  public async createContact(data: CreateContactRequest, token: string) {
    return await (
      await publicApiRequest()
    ).post('/saved-contacts/api/contacts', data, addTokenToHeaders(token))
  }

  /**
   * Update an existing contact
   */
  public async updateContact(contactId: number, data: UpdateContactRequest, token: string) {
    return await (
      await publicApiRequest()
    ).put(`/saved-contacts/api/contacts/${contactId}`, data, addTokenToHeaders(token))
  }

  /**
   * Delete a contact
   */
  public async deleteContact(contactId: number, token: string) {
    return await (
      await publicApiRequest()
    ).delete(`/saved-contacts/api/contacts/${contactId}`, addTokenToHeaders(token))
  }

  /**
   * Increment usage count for a contact (called after successful payment)
   */
  public async incrementUsage(contactId: number, token: string) {
    return await (
      await publicApiRequest()
    ).post(`/saved-contacts/api/contacts/${contactId}/increment-usage`, {}, addTokenToHeaders(token))
  }

  /**
   * Find a contact by phone and payment method
   */
  public async findContact(data: FindContactRequest, token: string) {
    return await (
      await publicApiRequest()
    ).post('/saved-contacts/api/contacts/find', data, addTokenToHeaders(token))
  }
}
